import { DocumentType, EmployerDocumentDto } from '@zorro/clients';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { DeepReadonly } from '@zorro/types';
import {
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import {
  DocumentDeleteModal,
  DocumentListSection,
} from '../DocumentListSection';
import { FullPageLoader } from '../FullPageLoader';
import { EmployerDocumentCard } from './EmployerDocumentCard';
import { EmployerDocumentForm } from './EmployerDocumentForm';

type Props = {
  employerId: string;
  documentType: DocumentType;
  openEnrollmentPeriodId: string | null;
};

const VISIBLE_TO_ADMINS_ONLY = '(visible to admins only)';

const documentTypeConfig: DeepReadonly<{
  [documentType in DocumentType]: {
    label: string;
    visibleTo: string;
    uploadTitleSuffix: string;
  };
}> = {
  [DocumentType.ADMIN]: {
    label: 'Admin documents',
    visibleTo: VISIBLE_TO_ADMINS_ONLY,
    uploadTitleSuffix: 'admin document',
  },
  [DocumentType.ZORRO_PAY]: {
    label: 'Payment reports',
    visibleTo: VISIBLE_TO_ADMINS_ONLY,
    uploadTitleSuffix: 'payment report',
  },
  [DocumentType.PLAN]: {
    label: 'Plan documents',
    visibleTo: '(Visible to everyone at the company)',
    uploadTitleSuffix: 'plan document',
  },
  [DocumentType.COMPLIANCE]: {
    label: 'Compliance reports',
    visibleTo: VISIBLE_TO_ADMINS_ONLY,
    uploadTitleSuffix: 'compliance report',
  },
  [DocumentType.INVOICE]: {
    label: 'Invoices',
    visibleTo: VISIBLE_TO_ADMINS_ONLY,
    uploadTitleSuffix: 'invoice',
  },
};

const getUploadDrawerTitle = (
  documentType: DocumentType,
  isEditMode: boolean
) => {
  const prefix = isEditMode ? 'Edit' : 'Add a new';
  const { uploadTitleSuffix } = documentTypeConfig[documentType];
  return `${prefix} ${uploadTitleSuffix}`;
};

const getIsEditable = (
  isZorroOperations: boolean,
  isAccountSupervisor: boolean,
  documentType: DocumentType
) => {
  if (
    documentType === DocumentType.ZORRO_PAY ||
    documentType === DocumentType.COMPLIANCE ||
    documentType === DocumentType.INVOICE
  ) {
    return isZorroOperations;
  }
  return isZorroOperations || isAccountSupervisor;
};

export const DocumentsPage = ({
  employerId,
  documentType,
  openEnrollmentPeriodId,
}: Props) => {
  const { isZorroOperations, isAccountSupervisor } = useRoles();

  const isEditable = getIsEditable(
    isZorroOperations,
    isAccountSupervisor,
    documentType
  );

  const [selectedDocument, setSelectedDocument] =
    useState<EmployerDocumentDto>();
  const [isDeleteDocumentsModalOpen, setIsDeleteDocumentsModalOpen] =
    useState(false);
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
    useState(false);
  const [uploadedFileFromEmptyState, setUploadedFileFromEmptyState] = useState<
    File | undefined
  >(undefined);

  const { mutate: deleteDocumentMutation } = useMonolithMutation({
    method: 'employerDocumentControllerDeleteDocument',
    successMessage: 'Document deleted!',
  });

  const { data: documents, isLoading: isDocumentLoading } = useMonolithQuery({
    method: 'employerDocumentControllerGetDocumentsByType',
    params: [employerId, documentType],
  });

  if (isDocumentLoading || !documents) {
    return <FullPageLoader />;
  }

  const filteredDocuments = documents
    .filter(
      (document) =>
        !document.openEnrollmentPeriodId ||
        document.openEnrollmentPeriodId === openEnrollmentPeriodId
    )
    .sort((first, second) => second.createdAt.localeCompare(first.createdAt));

  const { label } = documentTypeConfig[documentType];

  // Scoped to what the tab shows. Documents from other plan years are filtered
  // out above, so a rename can still collide with one the viewer cannot see.
  const existingFileNames = filteredDocuments.map(
    (document) => document.fileName
  );

  const resetModalsAndSelections = () => {
    setSelectedDocument(undefined);
    setUploadedFileFromEmptyState(undefined);
    setIsUploadDocumentModalOpen(false);
    setIsDeleteDocumentsModalOpen(false);
  };

  return (
    <>
      <DocumentListSection
        title={label}
        subtitle={documentTypeConfig[documentType].visibleTo}
        canManage={isEditable}
        isEmpty={filteredDocuments.length === 0}
        onAddClick={() => setIsUploadDocumentModalOpen(true)}
        onEmptyUpload={(files) => {
          setUploadedFileFromEmptyState(files[0]);
          setIsUploadDocumentModalOpen(true);
        }}
        readOnlyEmptySubtitle="We haven't added anything yet. Please check back soon!"
      >
        {filteredDocuments.map((document) => (
          <EmployerDocumentCard
            key={document.id}
            document={document}
            isEditable={isEditable}
            onEdit={() => {
              setSelectedDocument(document);
              setIsUploadDocumentModalOpen(true);
            }}
            onDelete={() => {
              setSelectedDocument(document);
              setIsDeleteDocumentsModalOpen(true);
            }}
          />
        ))}
      </DocumentListSection>

      <Drawer
        opened={isUploadDocumentModalOpen}
        onClose={resetModalsAndSelections}
        title={getUploadDrawerTitle(documentType, Boolean(selectedDocument))}
      >
        <EmployerDocumentForm
          employerId={employerId}
          type={documentType}
          existingDocument={selectedDocument}
          existingFileNames={existingFileNames}
          viewedOpenEnrollmentPeriodId={openEnrollmentPeriodId ?? undefined}
          onSuccess={resetModalsAndSelections}
          onCancel={resetModalsAndSelections}
          uploadedFile={uploadedFileFromEmptyState}
        />
      </Drawer>
      <DocumentDeleteModal
        isOpen={isDeleteDocumentsModalOpen}
        fileName={selectedDocument?.fileName}
        onConfirm={async () => {
          if (selectedDocument) {
            await deleteDocumentMutation([employerId, selectedDocument.id]);
          }
          resetModalsAndSelections();
        }}
        onClose={resetModalsAndSelections}
      />
    </>
  );
};
